import { useEffect, useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { useAuth } from '@/context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { formatPrice } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Download, FileSpreadsheet, FileText, ArrowLeft } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, WidthType, BorderStyle } from 'docx';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminBuyers() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    if (!user.is_admin) {
      navigate('/');
      toast.error('Admin access required');
      return;
    }
    fetchBuyers();
  }, [user, authLoading, navigate]);

  const fetchBuyers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/buyers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuyers(response.data);
    } catch (error) {
      console.error('Error fetching buyers:', error);
      toast.error('Failed to load buyers');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    const data = buyers.map(buyer => ({
      'Name': buyer.name,
      'Email': buyer.email,
      'Phone': buyer.phone,
      'Alternate Phone': buyer.alternate_phone,
      'Billing Address': buyer.billing_address,
      'Delivery Address': buyer.delivery_address,
      'GST Number': buyer.gst_number,
      'Total Orders': buyer.total_orders,
      'Total Spent': buyer.total_spent,
      'First Order Date': buyer.first_order_date ? new Date(buyer.first_order_date).toLocaleDateString() : '',
      'Last Order Date': buyer.last_order_date ? new Date(buyer.last_order_date).toLocaleDateString() : ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Buyers');
    
    // Auto-size columns
    const colWidths = Object.keys(data[0] || {}).map(key => ({ wch: Math.max(key.length, 20) }));
    worksheet['!cols'] = colWidths;

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `MFS_Buyers_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel file downloaded!');
  };

  const downloadWord = async () => {
    const tableRows = [
      // Header row
      new TableRow({
        children: [
          'Name', 'Email', 'Phone', 'Address', 'Total Orders', 'Total Spent'
        ].map(text => new TableCell({
          children: [new Paragraph({
            children: [new TextRun({ text, bold: true, size: 20 })]
          })],
          width: { size: 1500, type: WidthType.DXA }
        }))
      }),
      // Data rows
      ...buyers.map(buyer => new TableRow({
        children: [
          buyer.name || '',
          buyer.email || '',
          buyer.phone || '',
          buyer.delivery_address || buyer.billing_address || '',
          String(buyer.total_orders || 0),
          `₹${(buyer.total_spent || 0).toLocaleString()}`
        ].map(text => new TableCell({
          children: [new Paragraph({
            children: [new TextRun({ text, size: 18 })]
          })],
          width: { size: 1500, type: WidthType.DXA }
        }))
      }))
    ];

    const doc = new Document({
      sections: [{
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: 'MFS Buyers List', bold: true, size: 32 })
            ],
            spacing: { after: 400 }
          }),
          new Paragraph({
            children: [
              new TextRun({ text: `Generated on: ${new Date().toLocaleDateString()}`, size: 20, italics: true })
            ],
            spacing: { after: 400 }
          }),
          new Paragraph({
            children: [
              new TextRun({ text: `Total Buyers: ${buyers.length}`, size: 22 })
            ],
            spacing: { after: 400 }
          }),
          new Table({
            rows: tableRows,
            width: { size: 100, type: WidthType.PERCENTAGE }
          })
        ]
      }]
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `MFS_Buyers_${new Date().toISOString().split('T')[0]}.docx`);
    toast.success('Word file downloaded!');
  };

  // Show loading while auth is being verified
  if (authLoading) {
    return (
      <div className="min-h-screen noise-texture flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground uppercase tracking-wider">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return null;
  }

  return (
    <div className="min-h-screen noise-texture">
      <Navigation />

      <div className="container mx-auto px-8 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard">
              <Button variant="outline" className="rounded-none">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <h1 className="text-4xl md:text-5xl font-black uppercase">BUYERS LIST</h1>
          </div>
          
          <div className="flex gap-3">
            <Button 
              onClick={downloadExcel}
              disabled={buyers.length === 0}
              className="bg-green-600 text-white hover:bg-green-700 rounded-none uppercase tracking-wider font-bold"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Download Excel
            </Button>
            <Button 
              onClick={downloadWord}
              disabled={buyers.length === 0}
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-none uppercase tracking-wider font-bold"
            >
              <FileText className="w-4 h-4 mr-2" />
              Download Word
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-border rounded-none p-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-primary" />
              <p className="mono text-3xl font-black">{buyers.length}</p>
            </div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">TOTAL BUYERS</p>
          </div>
          <div className="bg-white border border-border rounded-none p-6">
            <div className="flex items-center justify-between mb-2">
              <Download className="w-8 h-8 text-primary" />
              <p className="mono text-3xl font-black">{buyers.reduce((sum, b) => sum + b.total_orders, 0)}</p>
            </div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">TOTAL ORDERS</p>
          </div>
          <div className="bg-white border border-border rounded-none p-6">
            <div className="flex items-center justify-between mb-2">
              <FileSpreadsheet className="w-8 h-8 text-primary" />
              <p className="mono text-2xl font-black">{formatPrice(buyers.reduce((sum, b) => sum + b.total_spent, 0))}</p>
            </div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">TOTAL REVENUE</p>
          </div>
        </div>

        {/* Buyers Table */}
        <div className="bg-white border border-border rounded-none p-8">
          <h2 className="text-2xl font-black uppercase mb-6">ALL BUYERS</h2>

          {loading ? (
            <p className="text-center py-12 text-muted-foreground">Loading buyers...</p>
          ) : buyers.length === 0 ? (
            <p className="text-center py-12 text-muted-foreground">No buyers found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="text-left py-4 px-2 text-xs uppercase tracking-wider font-black">Name</th>
                    <th className="text-left py-4 px-2 text-xs uppercase tracking-wider font-black">Email</th>
                    <th className="text-left py-4 px-2 text-xs uppercase tracking-wider font-black">Phone</th>
                    <th className="text-left py-4 px-2 text-xs uppercase tracking-wider font-black">Address</th>
                    <th className="text-center py-4 px-2 text-xs uppercase tracking-wider font-black">Orders</th>
                    <th className="text-right py-4 px-2 text-xs uppercase tracking-wider font-black">Total Spent</th>
                  </tr>
                </thead>
                <tbody>
                  {buyers.map((buyer, index) => (
                    <tr key={index} className="border-b border-border hover:bg-muted/50">
                      <td className="py-4 px-2">
                        <p className="font-bold">{buyer.name || '-'}</p>
                      </td>
                      <td className="py-4 px-2">
                        <p className="text-sm">{buyer.email || '-'}</p>
                      </td>
                      <td className="py-4 px-2">
                        <p className="text-sm mono">{buyer.phone || '-'}</p>
                        {buyer.alternate_phone && (
                          <p className="text-xs text-muted-foreground mono">{buyer.alternate_phone}</p>
                        )}
                      </td>
                      <td className="py-4 px-2">
                        <p className="text-sm max-w-xs truncate" title={buyer.delivery_address || buyer.billing_address}>
                          {buyer.delivery_address || buyer.billing_address || '-'}
                        </p>
                      </td>
                      <td className="py-4 px-2 text-center">
                        <span className="bg-primary/10 text-primary font-bold px-3 py-1 rounded-full text-sm">
                          {buyer.total_orders}
                        </span>
                      </td>
                      <td className="py-4 px-2 text-right">
                        <p className="font-bold mono">{formatPrice(buyer.total_spent)}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
